import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Badge } from 'react-bootstrap';
import { ArrowLeft } from 'react-bootstrap-icons';
import { fetchMyOrderDetail } from '../services/api';
import FroggySpinner from '../components/common/FroggySpinner';
import { formatCurrency } from '../utils/currency';
import { buildAssetUrl, retryThenTile } from '../utils/imageResolver';
import { NO_PHOTO_SRC } from '../constants/noPhoto';
import { computeDeliveryDisplay, requiresHubSelection, getHubAddressDisplay } from '../utils/deliveryHelpers';
import { formatDateTime } from '../utils/dateUtils';
import { renderVariationLines } from '../utils/variationHelpers';
// #68 named offers + the free-shipping line; shape notes live with the
// helpers (was a per-page copy until ShipmentDrawer became the 4th surface).
import { shippingDiscountOf, shippingOfferName, namedPromotions } from '../utils/promotionDisplay';
import '../styles/orders.css';
import '../styles/cart.css'; // #90b shared .discount-breakdown classes

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrderDetail();
  }, [orderId]);

  const loadOrderDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchMyOrderDetail(orderId);
      setOrder(response.order);
      
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Order not found or you do not have permission to view this order.');
      } else {
        setError('Failed to load order details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDeliveryDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
      // Parse the date string and format it for display
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (err) {
      return dateStr;
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'PAID':
        return 'success';
      case 'AWAITING_CUSTOMER_SCHEDULING':
        return 'info';
      case 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER':
        return 'warning';
      case 'AWAITING_STORE_SCHEDULING':
        return 'primary';
      case 'SHIPPED':
        return 'success';
      case 'DELIVERED':
        return 'success';
      case 'CANCELLED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusDisplay = (status) => {
    switch (status) {
      case 'AWAITING_CUSTOMER_SCHEDULING':
        return 'Awaiting Schedule';
      case 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER':
        return 'Rescheduling Required (Weather)';
      case 'AWAITING_STORE_SCHEDULING':
        return 'Being Prepared';
      default:
        return status?.replace(/_/g, ' ') || 'Unknown';
    }
  };


  // Helper functions for scheduling CTA
  const isSchedulable = (order) => {
    return order.status === 'AWAITING_CUSTOMER_SCHEDULING' || 
           order.status === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER';
  };

  const getSchedulingTitle = (order) => {
    if (order.status === 'AWAITING_CUSTOMER_SCHEDULING') {
      return 'Action Required: Schedule Your Delivery';
    } else if (order.status === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER') {
      return '🌡️ Weather Update: Reschedule Required';
    }
    return '';
  };

  const getSchedulingMessage = (order) => {
    if (order.status === 'AWAITING_CUSTOMER_SCHEDULING') {
      return 'Your order is ready! Please select your preferred delivery date to continue.';
    } else if (order.status === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER') {
      return 'Your original delivery date has unsafe temperatures. Please choose a new safe arrival date below.';
    }
    return '';
  };

  const getSchedulingButtonLabel = (order) => {
    if (order.status === 'AWAITING_CUSTOMER_SCHEDULING') {
      return 'Schedule Delivery Date';
    } else if (order.status === 'REQUIRES_CUSTOMER_RESCHEDULING_DUE_TO_WEATHER') {
      // Use delivery helper for consistent labeling
      if (requiresHubSelection(order)) {
        return `Choose a New ${computeDeliveryDisplay(order).label} Date`;
      } else {
        return 'Choose a New Delivery Date';
      }
    }
    return '';
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <FroggySpinner size="lg" label="Loading order details" />
      </Container>
    );
  }

  if (error || !order) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Unable to Load Order</Alert.Heading>
          <p>{error || 'Order details are not available.'}</p>
          <hr />
          <div className="d-flex justify-content-between">
            <button type="button" className="ff-btn ff-btn--secondary" onClick={() => navigate('/account/orders')}>
              <ArrowLeft />
              Back to Orders
            </button>
            <button type="button" className="ff-btn ff-btn--secondary" onClick={loadOrderDetail}>
              Try Again
            </button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={8}>
          {/* Back Button */}
          <button
            type="button"
            className="ff-btn ff-btn--secondary mb-4"
            onClick={() => navigate('/account/orders')}
          >
            <ArrowLeft />
            Back to Orders
          </button>

          {/* Order Header */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Order Details</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p><strong>Order Number:</strong> {order.id}</p>
                  {order.created_at && (
                    <p><strong>Order Date:</strong> {formatDateTime(order.created_at)}</p>
                  )}
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Order Status:</strong>{' '}
                    <Badge bg={getStatusVariant(order.status)} className="ms-1">
                      {getStatusDisplay(order.status)}
                    </Badge>
                    {order.payment_status && order.payment_status !== 'PAID' && (
                      <>
                        {' '}
                        <Badge bg="warning" className="ms-1">
                          Payment: {order.payment_status}
                        </Badge>
                      </>
                    )}
                  </p>
                  {order.delivery_date && (
                    <p><strong>Expected Delivery:</strong> {formatDeliveryDate(order.delivery_date)}</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Line Items */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Items Ordered</h4>
            </Card.Header>
            <Card.Body className="p-0">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={item.id || index} className="border-bottom p-3">
                    <Row className="align-items-center">
                      <Col md={2}>
                        {(() => {
                          const variation = item.product_variation;
                          const morph = variation?.morph;
                          const species = variation?.product?.species_id;
                          
                          // Full cascade hierarchy: variation → morph → species → placeholder
                          let imageUrl = null;
                          let source = 'placeholder';
                          
                          // Priority 1: Variation images
                          if (variation?.cover_display_url) {
                            imageUrl = buildAssetUrl(variation.cover_display_url);
                            source = 'variation.cover_display_url';
                          } else if (variation?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(variation.cover_thumb_url);
                            source = 'variation.cover_thumb_url';
                          } else if (variation?.image_url) {
                            imageUrl = buildAssetUrl(variation.image_url);
                            source = 'variation.image_url';
                          }
                          // Priority 2: Morph images
                          else if (morph?.cover_display_url) {
                            imageUrl = buildAssetUrl(morph.cover_display_url);
                            source = 'morph.cover_display_url';
                          } else if (morph?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(morph.cover_thumb_url);
                            source = 'morph.cover_thumb_url';
                          } else if (morph?.image_url) {
                            imageUrl = buildAssetUrl(morph.image_url);
                            source = 'morph.image_url';
                          }
                          // Priority 3: Species images
                          else if (species?.cover_display_url) {
                            imageUrl = buildAssetUrl(species.cover_display_url);
                            source = 'species.cover_display_url';
                          } else if (species?.cover_thumb_url) {
                            imageUrl = buildAssetUrl(species.cover_thumb_url);
                            source = 'species.cover_thumb_url';
                          } else if (species?.image_url) {
                            imageUrl = buildAssetUrl(species.image_url);
                            source = 'species.image_url';
                          }
                          
                          // Fallback to placeholder
                          const finalUrl = imageUrl || NO_PHOTO_SRC;
                          
                          return (
                            <img 
                              src={finalUrl}
                              alt={item.name}
                              className="img-fluid rounded"
                              data-no-photo={imageUrl ? undefined : '1'}
                              onError={imageUrl ? retryThenTile : undefined}
                              style={{ maxHeight: '60px', objectFit: imageUrl ? 'cover' : 'contain' }}
                            />
                          );
                        })()}
                      </Col>
                      <Col md={6}>
                        <h6 className="mb-1">{item.name || 'Product'}</h6>
                        {renderVariationLines(item.product_variation)}
                        <small className="text-muted">Unit Price: {formatCurrency(item.unit_price)}</small>
                      </Col>
                      <Col md={2} className="text-center">
                        <strong>Qty: {item.quantity}</strong>
                      </Col>
                      <Col md={2} className="text-end">
                        <strong>{formatCurrency(item.line_total)}</strong>
                      </Col>
                    </Row>
                  </div>
                ))
              ) : (
                <div className="p-3 text-muted">No items found</div>
              )}
            </Card.Body>
          </Card>

          {/* Order Totals & Shipping */}
          <Card className="mb-4">
            <Card.Header>
              <h4 className="mb-0">Order Summary</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  {/* Shipping Information */}
                  <h6>Shipping Method</h6>
                  <p className="mb-1">
                    <strong>
                      {computeDeliveryDisplay(order).icon} {computeDeliveryDisplay(order).label}
                    </strong>
                  </p>
                  {order.shipping_summary?.carrier && (
                    <p className="mb-1">
                      Carrier: {order.shipping_summary.carrier} - {order.shipping_summary.service}
                    </p>
                  )}
                  {order.box_size_final_label && (
                    <p className="mb-1">
                      <strong>Box Size:</strong> {order.box_size_final_label}
                    </p>
                  )}
                  {order.original_box_size_label && order.original_box_size_label !== order.box_size_final_label && (
                    <p className="mb-1 text-muted">
                      <strong>Original Box Size:</strong> {order.original_box_size_label}
                    </p>
                  )}
                  
                  {/* Hub Pickup Address Block */}
                  {requiresHubSelection(order) && order.hub_address && (
                    <div className="mt-3 p-3 bg-light rounded">
                      <h6 className="text-primary mb-2">📦 {computeDeliveryDisplay(order).label}</h6>
                      <div className="fw-bold">{order.hub_address.hub_name}</div>
                      <div className="small text-muted mt-1">
                        {order.hub_address.street1 && <div>{order.hub_address.street1}</div>}
                        {order.hub_address.street2 && <div>{order.hub_address.street2}</div>}
                        <div>
                          {order.hub_address.city && `${order.hub_address.city}, `}
                          {order.hub_address.state && `${order.hub_address.state} `}
                          {order.hub_address.zip}
                        </div>
                      </div>
                      <small className="text-muted">FedEx hub pickup (details will be provided with tracking)</small>
                    </div>
                  )}
                  
                  {/* Legacy fallback for pickup destination type */}
                  {!requiresHubSelection(order) && order.shipping_summary?.destination_type === 'pickup' && (
                    <p className="mb-0">
                      <small className="text-muted">{computeDeliveryDisplay(order).label} (details via email)</small>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {/* Order Totals */}
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(order.subtotal)}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Shipping:</span>
                    <span>{formatCurrency(order.shipping_total)}</span>
                  </div>

                  {shippingDiscountOf(order) > 0 && (
                    <div className="d-flex justify-content-between mb-2">
                      <span style={{ color: 'var(--ff-semantic-success)' }}>
                        Shipping discount{shippingOfferName(order) ? ` (${shippingOfferName(order)})` : ''}:
                      </span>
                      <span style={{ color: 'var(--ff-semantic-success)' }}>
                        -{formatCurrency(shippingDiscountOf(order))}
                      </span>
                    </div>
                  )}


                  {/* Discount Breakdown — #90b shared .discount-breakdown
                      classes (cart.css); --plain keeps this column's
                      unboxed, page-size rendering. */}
                  {order.total_discount && parseFloat(order.total_discount) > 0 && (
                    <>
                      {order.auto_discount && parseFloat(order.auto_discount) > 0 && (
                        <div className="discount-breakdown__row discount-breakdown__row--plain">
                          <span className="discount-breakdown__amount">Automatic discount:</span>
                          <span className="discount-breakdown__amount">-{formatCurrency(order.auto_discount)}</span>
                        </div>
                      )}
                      {order.promo_code_discount && parseFloat(order.promo_code_discount) > 0 && (
                        <div className="discount-breakdown__row discount-breakdown__row--plain">
                          <span className="discount-breakdown__amount">Promo code:</span>
                          <span className="discount-breakdown__amount">-{formatCurrency(order.promo_code_discount)}</span>
                        </div>
                      )}
                      {/* #68 — name the offers behind those amounts, same
                          bullet treatment the cart and checkout use. */}
                      {(() => {
                        const offers = namedPromotions(order.applied_promotions);
                        if (!offers.length) return null;
                        return (
                          <div className="discount-breakdown__promos">
                            {offers.map((promo, idx) => (
                              <div key={idx} className="discount-breakdown__promo-line">
                                • {promo.name}
                                {Number.isFinite(promo.amount) && (
                                  <span className="discount-breakdown__amount">
                                    {' '}−{formatCurrency(promo.amount)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>Total Paid:</strong>
                    <strong>{formatCurrency(order.order_total)}</strong>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Schedule/Reschedule Delivery CTA for schedulable orders */}
          {isSchedulable(order) && (
            <Card className="mb-4 border-info">
              <Card.Body className="text-center">
                <h5>{getSchedulingTitle(order)}</h5>
                <p className="mb-3">
                  {getSchedulingMessage(order)}
                </p>
                {/* R5 B5: scheduling is the page's one main act when it is
                    owed — the ff-btn primary at the standard 36px */}
                <button
                  type="button"
                  className="ff-btn ff-btn--primary"
                  onClick={() => navigate(`/account/orders/${orderId}/schedule`)}
                >
                  {getSchedulingButtonLabel(order)}
                </button>
              </Card.Body>
            </Card>
          )}

          {/* What Happens Next - show for paid orders that don't need scheduling */}
          {order.payment_status === 'PAID' && !isSchedulable(order) && (
            <Card className="mb-4">
              <Card.Body>
                <h5 className="order-next-steps-title">What happens next?</h5>
                <ul className="order-next-steps-list">
                  {order.status === 'AWAITING_STORE_SCHEDULING' ? (
                    <>
                      <li className="order-next-steps-item step-preparation">We're preparing your animals for safe transport</li>
                      <li className="order-next-steps-item step-shipping">Tracking information once your order ships</li>
                      <li className="order-next-steps-item step-shipping">Priority Overnight delivery on your scheduled date</li>
                    </>
                  ) : (
                    <>
                      <li className="order-next-steps-item step-confirmation">Order confirmation & scheduling email to coordinate delivery details</li>
                      <li className="order-next-steps-item step-preparation">We'll prepare your animals for safe transport</li>
                      <li className="order-next-steps-item step-shipping">Tracking information once your order ships</li>
                      <li className="order-next-steps-item step-pickup">Pickup instructions (if applicable)</li>
                    </>
                  )}
                </ul>
              </Card.Body>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="text-center">
            <button
              type="button"
              className="ff-btn ff-btn--secondary me-3"
              onClick={() => navigate('/account/orders')}
            >
              <ArrowLeft />
              Back to Orders
            </button>
            <button
              type="button"
              className="ff-btn ff-btn--secondary"
              onClick={() => navigate('/')}
            >
              Continue Shopping
            </button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default OrderDetailPage;